import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { GlobalUser } from '../layout/model/global-user';

function getWindow(): any {
  return window;
}

// Read by index.html before Crisp's l.js loads, so a returning visitor's
// session is restored from the start instead of via a reset.
const TOKEN_ID_KEY = 'crisp_token_id';
// Email of the user the current Crisp session belongs to. Lets us detect an
// account switch even when neither user has a token (where comparing tokens
// alone would see undefined === undefined and keep the old session).
const IDENTIFIED_EMAIL_KEY = 'crisp_identified_email';

@Injectable({
  providedIn: 'root'
})
export class CrispWidgetService {
  /** Fires on "chat:opened"/"chat:closed" — including when the visitor
   *  closes the panel some other way than our own button (e.g. a native
   *  Crisp close icon that slips past _hideNativeCloseButton) — so our
   *  custom button stays in sync regardless of how the panel got closed. */
  readonly opened$ = new Subject<void>();
  readonly closed$ = new Subject<void>();

  /** Fires on "message:received" — a message the operator sent to the
   *  visitor — so the page can surface an unread badge when the chat isn't
   *  currently open. */
  readonly operatorMessage$ = new Subject<void>();

  /** Fires when we reset the Crisp session (account switch / token change /
   *  logout). The reset closes and hides the panel without a "chat:closed"
   *  event, so the page uses this to put its own button state back. */
  readonly sessionReset$ = new Subject<void>();

  // Last identity pushed via identify(). Re-applied on every
  // "session:loaded", since a session:reset reloads the chatbox
  // asynchronously and may drop user data set right after it.
  private _identity: { emailArgs: string[]; nickname?: string } | null = null;

  constructor() {
    // chat:hide hides Crisp's whole container (launcher + panel) — it does
    // NOT get overridden by chat:open, so opening always needs an explicit
    // show() first. To keep the real launcher from reappearing once the
    // chat is closed (by us, or by the user clicking Crisp's own close
    // icon inside the panel), re-hide on every "chat:closed" event rather
    // than only on our own close() calls.
    getWindow().$crisp?.push(['on', 'chat:opened', () => this.opened$.next()]);
    // Guarded against re-entrancy: if Crisp itself ever emits "chat:closed"
    // as a side effect of the chat:hide push below (undocumented either
    // way), this stops that from becoming a self-triggering loop.
    let handlingClose = false;
    getWindow().$crisp?.push(['on', 'chat:closed', () => {
      if (handlingClose) {
        return;
      }
      handlingClose = true;
      this.hide();
      this.closed$.next();
      Promise.resolve().then(() => (handlingClose = false));
    }]);
    getWindow().$crisp?.push(['on', 'message:received', () => this.operatorMessage$.next()]);
    getWindow().$crisp?.push(['on', 'session:loaded', () => this._pushIdentity()]);
    this._hideNativeCloseButton();
  }

  /**
   * Best-effort removal of Crisp's own in-panel close icon, its help/FAQ
   * (Helpdesk) tab icon, and its floating launcher bubble, so our custom
   * toggle button is the only control shown. Crisp renders its panel as
   * plain DOM (not an iframe) inside #crisp-chatbox but with hashed,
   * version-dependent class names, so instead of guessing at those we
   * watch for: (a) an interactive control carrying an aria-label/title
   * matching "close" or "help" as a whole word, and (b) the launcher
   * toggle, which always carries a `data-pop` attribute regardless of
   * version. Both get an inline `display:none !important` — that wins
   * even if Crisp itself later sets a competing inline style (e.g. a
   * proactive campaign forcing the launcher back open), since an inline
   * !important always outranks a stylesheet !important. Scoped to
   * #crisp-chatbox itself (not the whole document) once it exists, so this
   * doesn't add a document-wide mutation listener that reruns on every
   * unrelated SPA DOM change.
   */
  private _hideNativeCloseButton(): void {
    const HIDDEN_LABEL = /\b(close|help)\b/i;
    // Setting `display` unconditionally on every pass would itself be a
    // `style` attribute mutation, re-triggering the attribute-watching
    // MutationObserver below forever. Only write when it isn't already
    // hidden the way we want, so a no-op pass makes no DOM changes.
    const hideEl = (el: HTMLElement) => {
      if (el.style.getPropertyValue('display') === 'none' && el.style.getPropertyPriority('display') === 'important') {
        return;
      }
      el.style.setProperty('display', 'none', 'important');
    };
    const hideMatches = (root: HTMLElement) => {
      root.querySelectorAll<HTMLElement>(
        'button[aria-label], a[aria-label], [role="button"][aria-label], button[title], a[title]'
      ).forEach((el) => {
        const label = el.getAttribute('aria-label') || el.getAttribute('title') || '';
        if (HIDDEN_LABEL.test(label)) {
          hideEl(el);
        }
      });
      root.querySelectorAll<HTMLElement>('[data-pop]').forEach(hideEl);
    };

    // Also re-run on `style` attribute changes, not just node insertions —
    // Crisp can force an already-hidden element back open by rewriting
    // `style.display` in place on the same node (e.g. a proactive campaign),
    // which is an attribute mutation that a childList-only observer misses.
    const observerOptions: MutationObserverInit = {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    };

    // Crisp's script appends #crisp-chatbox to <body> once it loads, and a
    // session:reset can rebuild it as a new node — so watch <body>'s direct
    // children only (not its subtree) and re-attach the scoped observer
    // whenever the root node changes, instead of observing the whole document.
    let observedRoot: HTMLElement | null = null;
    let rootObserver: MutationObserver | null = null;
    const attachToRoot = () => {
      const root = document.getElementById('crisp-chatbox');
      if (!root || root === observedRoot) {
        return;
      }
      rootObserver?.disconnect();
      observedRoot = root;
      hideMatches(root);
      rootObserver = new MutationObserver(() => hideMatches(root));
      rootObserver.observe(root, observerOptions);
    };
    attachToRoot();
    new MutationObserver(attachToRoot).observe(document.body, { childList: true });
  }

  show(): void {
    getWindow().$crisp?.push(['do', 'chat:show']);
  }

  hide(): void {
    getWindow().$crisp?.push(['do', 'chat:hide']);
  }

  open(): void {
    getWindow().$crisp?.push(['do', 'chat:show']);
    getWindow().$crisp?.push(['do', 'chat:open']);
  }

  close(): void {
    getWindow().$crisp?.push(['do', 'chat:close']);
    getWindow().$crisp?.push(['do', 'chat:hide']);
  }

  /**
   * Ties the Crisp session to the logged-in user so their conversation
   * history follows them across browsers/devices. Crisp keys history to a
   * per-browser session cookie, and `user:email` alone does not merge
   * sessions — so we use Crisp's session continuity token (CRISP_TOKEN_ID).
   *
   * The token is a random secret issued by the backend (`crisps_token` on
   * the current_user API): anyone holding it can read that user's chat, so
   * it must never be derived from guessable data on the client. A null or
   * empty token falls back to Crisp's default per-browser session (safe,
   * just not synced).
   *
   * The session is reset whenever the token differs from the one the
   * chatbox loaded with (first visit on this browser), or the account
   * differs from the one the session was last identified as — the latter
   * also covers users without a token, so histories never bleed across
   * accounts on a shared machine.
   */
  identify(user: GlobalUser): void {
    // Raw email goes to Crisp (the backend's signature is computed over it);
    // the normalized form is only for detecting an account switch.
    const email = user?.email;
    const normalizedEmail = email?.trim().toLowerCase();
    if (!email || !normalizedEmail) {
      return;
    }
    const lastEmail = localStorage.getItem(IDENTIFIED_EMAIL_KEY);
    const switchedUser = !!lastEmail && lastEmail.trim().toLowerCase() !== normalizedEmail;
    if (user.crisps_token !== undefined) {
      this._applyToken(user.crisps_token || undefined, switchedUser);
    } else if (switchedUser) {
      // Payloads without the key (e.g. the login response, or an `account`
      // cached before this field existed) can't tell us this user's token,
      // but the open session is someone else's — drop it now and let the
      // next current_user response apply the right token.
      this._applyToken(undefined, true);
    }
    // Otherwise (same user, key absent) the current session is already
    // theirs — leave it untouched rather than resetting it needlessly.
    localStorage.setItem(IDENTIFIED_EMAIL_KEY, normalizedEmail);
    // Safe to label the session now: it is either freshly reset or already
    // belongs to this user. With a signature, Crisp marks the email as
    // verified (identity verification), so operators can trust it.
    this._identity = {
      emailArgs: user.crisp_email_signature ? [email, user.crisp_email_signature] : [email],
      nickname: user.first_name || undefined,
    };
    this._pushIdentity();
  }

  private _pushIdentity(): void {
    if (!this._identity) {
      return;
    }
    const window = getWindow();
    window.$crisp?.push(['set', 'user:email', this._identity.emailArgs]);
    if (this._identity.nickname) {
      window.$crisp?.push(['set', 'user:nickname', [this._identity.nickname]]);
    }
  }

  /**
   * Drops the logged-in user's Crisp session from this browser, so the next
   * person on a shared machine can't reopen it. Called on logout. Resets
   * whenever any session was identified — with or without a token.
   */
  clearSession(): void {
    const hadSession =
      !!getWindow().CRISP_TOKEN_ID ||
      !!localStorage.getItem(TOKEN_ID_KEY) ||
      !!localStorage.getItem(IDENTIFIED_EMAIL_KEY);
    localStorage.removeItem(IDENTIFIED_EMAIL_KEY);
    // Never re-label the next (anonymous) session with the old user.
    this._identity = null;
    if (hadSession) {
      this._applyToken(undefined, true);
    }
  }

  /** Single place that switches the session token: updates CRISP_TOKEN_ID
   *  and its persisted copy, then resets Crisp so it reloads the session
   *  bound to that token. Skipped when nothing changed, unless forced. */
  private _applyToken(token: string | undefined, forceReset = false): void {
    const window = getWindow();
    if (!forceReset && window.CRISP_TOKEN_ID === token) {
      return;
    }
    window.CRISP_TOKEN_ID = token;
    if (token) {
      localStorage.setItem(TOKEN_ID_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_ID_KEY);
    }
    this._resetSession();
  }

  private _resetSession(): void {
    getWindow().$crisp?.push(['do', 'session:reset']);
    // session:reset reloads the chatbox, which can bring the launcher
    // back — the page reveals it explicitly via open().
    this.hide();
    this.sessionReset$.next();
  }
}
